import { isArray, isObject, isString } from './is';

/**
 * Nil-tolerant collection and string helpers.
 *
 * @remarks
 * Every helper accepts null, undefined or a value of the wrong type and returns a neutral
 * result ([], {}, '', undefined, false, 0) instead of throwing. Use them where the input comes
 * from props, state or an API response and is not guaranteed by the surrounding code.
 */

type Maybe<T> = T | null | undefined;

const toArray = <T>(list: Maybe<readonly T[]>): readonly T[] => (isArray(list) ? (list as readonly T[]) : []);

/**
 * @returns the mapped array, or [] if list is not an array
 */
export const safeMapArray = <T, U>(list: Maybe<readonly T[]>, fn: (item: T, index: number) => U): U[] =>
  toArray(list).map((item, index) => fn(item, index));

/**
 * @returns the mapped and flattened (one level) array, or [] if list is not an array
 */
export const safeFlatMap = <T, U>(list: Maybe<readonly T[]>, fn: (item: T, index: number) => U | readonly U[]): U[] =>
  toArray(list).flatMap((item, index) => fn(item, index));

/**
 * @returns the items matching the predicate, or [] if list is not an array
 */
export function safeFilter<T, S extends T>(
  list: Maybe<readonly T[]>,
  predicate: (item: T, index: number) => item is S,
): S[];
export function safeFilter<T>(list: Maybe<readonly T[]>, predicate: (item: T, index: number) => unknown): T[];
export function safeFilter<T>(list: Maybe<readonly T[]>, predicate: (item: T, index: number) => unknown): T[] {
  return toArray(list).filter((item, index) => predicate(item, index));
}

/**
 * @returns the first item matching the predicate, or undefined if none match or list is not an array
 */
export const safeFind = <T>(list: Maybe<readonly T[]>, predicate: (item: T, index: number) => unknown): T | undefined =>
  toArray(list).find((item, index) => predicate(item, index));

/**
 * @returns true if any item matches the predicate, false if none match or list is not an array
 */
export const safeSome = <T>(list: Maybe<readonly T[]>, predicate: (item: T, index: number) => unknown): boolean =>
  toArray(list).some((item, index) => predicate(item, index));

/**
 * @returns the first item, or undefined if list is empty or not an array
 */
export const safeHead = <T>(list: Maybe<readonly T[]>): T | undefined => toArray(list)[0];

/**
 * @returns the last item, or undefined if list is empty or not an array
 */
export const safeLast = <T>(list: Maybe<readonly T[]>): T | undefined => {
  const array = toArray(list);
  return array[array.length - 1];
};

/**
 * @returns the length of an array or string, or 0 for anything else
 */
export const safeLength = (list: Maybe<readonly unknown[] | string>): number =>
  isArray(list) || isString(list) ? list.length : 0;

/**
 * @returns true if the array contains value (or the string contains the substring), false for anything else
 */
export function safeIncludes(list: Maybe<string>, value: string): boolean;
export function safeIncludes<T>(list: Maybe<readonly T[]>, value: unknown): value is T;
export function safeIncludes<T>(list: Maybe<readonly T[] | string>, value: unknown): boolean {
  if (isString(list)) {
    return isString(value) && list.includes(value);
  }
  return toArray(list).includes(value as T);
}

/**
 * @returns the sum of the numbers, or 0 if list is empty or not an array
 */
export const safeSum = (list: Maybe<readonly number[]>): number => toArray(list).reduce((total, n) => total + n, 0);

/**
 * @returns a sorted copy (list is not mutated) ordered ascending by the key, or [] if list is not an array
 */
export const safeSortBy = <T>(list: Maybe<readonly T[]>, getKey: (item: T) => string | number): T[] =>
  [...toArray(list)].sort((a, b) => {
    const keyA = getKey(a);
    const keyB = getKey(b);
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  });

/**
 * @returns the object's own enumerable keys, or [] if it is not an object
 */
export const safeKeys = <T extends object>(obj: Maybe<T>): (keyof T & string)[] =>
  (isObject(obj) ? Object.keys(obj) : []) as (keyof T & string)[];

/**
 * @returns the object's own enumerable values, or [] if it is not an object
 */
export const safeValues = <T extends object>(obj: Maybe<T>): T[keyof T][] =>
  (isObject(obj) ? Object.values(obj) : []) as T[keyof T][];

/**
 * @returns the object's own enumerable [key, value] pairs, or [] if it is not an object
 */
export const safeEntries = <T extends object>(obj: Maybe<T>): [keyof T & string, T[keyof T]][] =>
  (isObject(obj) ? Object.entries(obj) : []) as [keyof T & string, T[keyof T]][];

/**
 * @returns a new object with every value mapped, or {} if obj is not an object
 */
export const safeMapValues = <T extends object, U>(
  obj: Maybe<T>,
  fn: (value: T[keyof T], key: keyof T & string) => U,
): Record<keyof T, U> =>
  Object.fromEntries(safeEntries(obj).map(([key, value]) => [key, fn(value, key)])) as Record<keyof T, U>;

/**
 * @returns the string split by separator, or [] if str is not a string
 */
export const safeSplit = (str: Maybe<string>, separator: string | RegExp): string[] =>
  isString(str) ? str.split(separator) : [];

/**
 * @returns true if str starts with prefix, false if either is not a string
 */
export const safeStartsWith = (str: Maybe<string>, prefix: Maybe<string>): boolean =>
  isString(str) && isString(prefix) && str.startsWith(prefix);

/**
 * @returns the lowercased string, or '' if str is not a string
 */
export const safeToLower = (str: Maybe<string>): string => (isString(str) ? str.toLowerCase() : '');
